import { CaretDownIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import {
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { MENU_ITEM_MOBILE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface ResponsiveMenuSubProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}

/**
 * A menu row that holds a long picker. Wide screens open it as a side list
 * capped to the room the screen has. On a phone a side list has nowhere to go
 * (the sidebar sheet leaves under 100px beside the menu), so the list opens
 * inline under its row instead.
 */
export function ResponsiveMenuSub({ icon, label, children }: ResponsiveMenuSubProps): JSX.Element {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);

  if (isMobile) {
    return (
      <>
        <DropdownMenuItem
          aria-expanded={expanded}
          onSelect={(event) => {
            event.preventDefault();
            setExpanded((value) => !value);
          }}
          className={cn(MENU_ITEM_MOBILE, 'rounded-sm')}
        >
          {icon}
          <span>{label}</span>
          <CaretDownIcon
            className={cn('ml-auto size-4 transition-transform', expanded && 'rotate-180')}
          />
        </DropdownMenuItem>
        {expanded ? (
          <div className="ml-3 max-h-64 overflow-y-auto overscroll-contain border-l border-border/40 pl-1">
            {children}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className={cn(MENU_ITEM_MOBILE, 'rounded-sm')}>
        {icon}
        <span>{label}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          sideOffset={8}
          alignOffset={-4}
          collisionPadding={16}
          className="w-[min(300px,var(--radix-dropdown-menu-content-available-width))] max-h-[min(320px,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto overscroll-contain"
        >
          {children}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
